import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  createBlogArticle,
  updateBlogArticle,
  fetchBlogAdminArticle,
} from "../../lib/adminApi";
import { fetchBlogCategories } from "../../lib/api";

export default function AdminBlogEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [categories, setCategories] = useState([]);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    cover_image_url: "",
    category: "guides",
    tags: "",
    status: "draft",
  });

  useEffect(() => {
    fetchBlogCategories().then((d) => setCategories(d.categories || [])).catch(() => {});
    if (isEdit) {
      fetchBlogAdminArticle(id)
        .then((d) => {
          const a = d.article;
          setForm({
            title: a.title || "",
            excerpt: a.excerpt || "",
            content: a.content || "",
            cover_image_url: a.cover_image_url || "",
            category: a.category || "guides",
            tags: (a.tags || []).join(", "),
            status: a.status || "draft",
          });
        })
        .catch(() => toast.error("Article introuvable"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (publishNow = false) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        cover_image_url: form.cover_image_url || null,
        category: form.category,
        tags: form.tags
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
      };
      if (!isEdit) {
        payload.status = publishNow ? "published" : "draft";
        await createBlogArticle(payload);
        toast.success(publishNow ? "Article publié" : "Brouillon enregistré");
      } else {
        await updateBlogArticle(id, payload);
        toast.success("Article mis à jour");
      }
      navigate("/admin/blog");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur";
      toast.error("Enregistrement impossible", { description: msg });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10">
        <div className="h-8 w-48 rounded shimmer" />
        <div className="mt-4 h-80 rounded shimmer" />
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-editor" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/admin/blog"
          data-testid="admin-blog-back"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-cyan-300"
        >
          <ArrowLeft className="h-3 w-3" /> Retour à la liste
        </Link>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="admin-blog-preview-toggle"
            onClick={() => setPreview(!preview)}
            className="gap-2 border-slate-700 bg-slate-900/60"
          >
            {preview ? <FileText className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {preview ? "Éditer" : "Prévisualiser"}
          </Button>
          <Button
            variant="outline"
            data-testid="admin-blog-save-draft"
            onClick={() => handleSave(false)}
            disabled={saving || !form.title || !form.content}
            className="gap-2 border-slate-700 bg-slate-900/60"
          >
            <Save className="h-4 w-4" /> {isEdit ? "Enregistrer" : "Brouillon"}
          </Button>
          {!isEdit && (
            <Button
              data-testid="admin-blog-publish"
              onClick={() => handleSave(true)}
              disabled={saving || !form.title || !form.content}
              className="gap-2 bg-cyan-500 text-slate-950 hover:bg-cyan-400"
            >
              <Eye className="h-4 w-4" /> Publier maintenant
            </Button>
          )}
        </div>
      </div>

      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-50">
        {isEdit ? "Modifier l'article" : "Nouvel article"}
      </h1>

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        {!preview ? (
          <div className="space-y-5 rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="space-y-1.5">
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                data-testid="editor-title"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                maxLength={180}
                placeholder="Ex. Comment reconnaître un filtre à huile compatible…"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="excerpt">Résumé (meta description SEO)</Label>
              <Textarea
                id="excerpt"
                data-testid="editor-excerpt"
                value={form.excerpt}
                onChange={(e) => update("excerpt", e.target.value)}
                maxLength={400}
                rows={2}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="content">Contenu (markdown) *</Label>
              <Textarea
                id="content"
                data-testid="editor-content"
                value={form.content}
                onChange={(e) => update("content", e.target.value)}
                rows={20}
                className="font-mono text-sm"
              />
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-8">
            <h2 className="font-heading text-3xl font-extrabold text-slate-50">
              {form.title || "(Sans titre)"}
            </h2>
            {form.excerpt && (
              <p className="mt-3 text-slate-400">{form.excerpt}</p>
            )}
            <div className="prose prose-invert prose-cyan mt-8 max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {form.content || "*Aucun contenu…*"}
              </ReactMarkdown>
            </div>
          </div>
        )}

        <aside className="space-y-5">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-cyan-400">
              Métadonnées
            </h3>
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => update("category", v)}>
                  <SelectTrigger data-testid="editor-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tags">Tags (séparés par virgules)</Label>
                <Input
                  id="tags"
                  data-testid="editor-tags"
                  value={form.tags}
                  onChange={(e) => update("tags", e.target.value)}
                  placeholder="pièce auto, OEM, SIV"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cover">Image de couverture (URL)</Label>
                <Input
                  id="cover"
                  data-testid="editor-cover"
                  value={form.cover_image_url}
                  onChange={(e) => update("cover_image_url", e.target.value)}
                  placeholder="https://…"
                />
                {form.cover_image_url && (
                  <img
                    src={form.cover_image_url}
                    alt="Prévisualisation couverture"
                    className="mt-2 w-full rounded-lg border border-slate-800 object-cover"
                  />
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
